/**
 * Hook globale per scorciatoie da tastiera in stile Superhuman/Gmail.
 *
 * Mappatura:
 *   Cmd/Ctrl+K     -> apre command palette
 *   c              -> nuovo messaggio (compose)
 *   r              -> rispondi al thread aperto
 *   e              -> archivia thread
 *   #              -> sposta nel cestino
 *   s              -> star/unstar thread
 *   u              -> toggle solo non letti
 *   j / k          -> messaggio successivo / precedente
 *   /              -> focus barra di ricerca
 *   ?              -> mostra dialog scorciatoie
 *   Esc            -> chiudi composer / dialog
 *   gi             -> vai a Inbox
 *   gs             -> vai a Inviati
 *   gd             -> vai a Bozze
 *   gt             -> vai a Cestino
 */
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate } from 'react-router-dom';
import { useComposer } from '@/stores/composer.store';
import { usePalette } from '@/stores/palette.store';
import { useInbox } from '@/stores/inbox.store';
import type { FolderRole } from '@/lib/api/endpoints';

export interface ShortcutContext {
  onArchive?: () => void;
  onTrash?: () => void;
  onStar?: () => void;
  onReply?: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  onShowHelp?: () => void;
  onFocusSearch?: () => void;
}

export function useKeyboardShortcuts(ctx: ShortcutContext = {}): void {
  const navigate = useNavigate();
  const openCompose = useComposer((s) => s.openCompose);
  const togglePalette = usePalette((s) => s.toggle);
  const setFolder = useInbox((s) => s.setFolder);
  const toggleUnread = useInbox((s) => s.toggleUnreadOnly);

  // Command palette: lavora ovunque, anche in input
  useHotkeys('mod+k', (e) => {
    e.preventDefault();
    togglePalette();
  }, { enableOnFormTags: true });

  // Help dialog
  useHotkeys('shift+slash', (e) => {
    e.preventDefault();
    ctx.onShowHelp?.();
  });

  // Compose
  useHotkeys('c', () => openCompose());

  // Reply, archive, trash, star
  useHotkeys('r', () => ctx.onReply?.());
  useHotkeys('e', () => ctx.onArchive?.());
  useHotkeys('shift+3', () => ctx.onTrash?.());
  useHotkeys('s', () => ctx.onStar?.());

  // Navigation
  useHotkeys('j', () => ctx.onNext?.());
  useHotkeys('k', () => ctx.onPrev?.());

  // Filtri inbox
  useHotkeys('u', () => toggleUnread());

  // Search focus
  useHotkeys('slash', (e) => {
    e.preventDefault();
    ctx.onFocusSearch?.();
  });

  // Goto folders (gi, gs, gd, gt) - sequenze in due tasti
  useHotkeys('g+i', () => {
    setFolder('inbox' as FolderRole);
    navigate('/');
  });
  useHotkeys('g+s', () => {
    setFolder('sent' as FolderRole);
    navigate('/');
  });
  useHotkeys('g+d', () => {
    setFolder('drafts' as FolderRole);
    navigate('/drafts');
  });
  useHotkeys('g+t', () => {
    setFolder('trash' as FolderRole);
    navigate('/');
  });
}
